
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { 
  UserProfile, 
  DatabaseUserProfile, 
  PaymentDetails, 
  PrivacySettings, 
  NotificationPreferences as NotificationPrefsType,
  Testimonial 
} from "@/types/profile";
import { Json } from "@/integrations/supabase/types";
import { ProfileForm } from "@/components/profile/ProfileForm";

export const ProfilePage = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<UserProfile>();

  const handleMediaUpload = async (url: string, type: 'profile' | 'cover') => {
    try {
      console.log(`handleMediaUpload called with url: ${url} and type: ${type}`);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No authenticated user found');
        toast({
          title: "Authentication Error",
          description: "You must be logged in to update your profile.",
          variant: "destructive",
        });
        return;
      }

      const updateField = type === 'profile' ? 'profile_pic' : 'cover_photo';
      console.log(`Updating field: ${updateField} with value: ${url}`);
      
      const { error, data } = await supabase
        .from('users')
        .update({ [updateField]: url })
        .eq('id', user.id)
        .select();

      console.log('Update response:', { error, data });

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      setProfile(prev => prev ? { ...prev, [updateField]: url } : null);
      setValue(updateField as keyof UserProfile, url);

      toast({
        title: "Success",
        description: `${type === 'profile' ? 'Profile picture' : 'Cover photo'} updated successfully`,
      });
    } catch (error: any) {
      console.error('Error updating media:', error);
      toast({
        title: "Error",
        description: "Failed to update image: " + (error.message || "Unknown error"),
        variant: "destructive",
      });
    }
  };

  const convertDatabaseToUserProfile = (dbProfile: DatabaseUserProfile): UserProfile => {
    const defaultPaymentDetails: PaymentDetails = {
      preferred_method: 'paypal',
      paypal_email: '',
    };

    const defaultPrivacySettings: PrivacySettings = {
      public_profile: true,
      show_contact: false,
      show_rates: false,
      enable_booking: true,
    };

    const notificationPrefs = dbProfile.notification_preferences as unknown as NotificationPrefsType || {
      email: true,
      push: true,
      sms: false,
    };

    const testimonials = (dbProfile.testimonials || []) as unknown as Testimonial[];
    const paymentDetails = dbProfile.payment_details as unknown as PaymentDetails || defaultPaymentDetails;
    const privacySettings = dbProfile.privacy_settings as unknown as PrivacySettings || defaultPrivacySettings;

    return {
      ...dbProfile,
      social_links: dbProfile.social_links as UserProfile['social_links'] || {},
      streaming_links: dbProfile.streaming_links as UserProfile['streaming_links'] || {},
      notification_preferences: notificationPrefs,
      testimonials: testimonials,
      role_types: dbProfile.role_types as UserProfile['role_types'] || [],
      privacy_settings: privacySettings,
      rate_card: dbProfile.rate_card as UserProfile['rate_card'] || {},
      availability: dbProfile.availability as UserProfile['availability'] || {
        weekdays: false,
        weekends: false,
        custom_dates: [],
      },
      payment_details: paymentDetails,
    };
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setError("Authentication error: Please sign in to view your profile");
          toast({
            title: "Authentication Error",
            description: "Please sign in to view your profile",
            variant: "destructive",
          });
          return;
        }

        const { data: dbProfile, error: fetchError } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (fetchError) throw fetchError;

        if (!dbProfile) {
          setError("Profile not found");
          toast({
            title: "Error",
            description: "Profile not found",
            variant: "destructive",
          });
          return;
        }

        const userProfile = convertDatabaseToUserProfile(dbProfile as DatabaseUserProfile);
        setProfile(userProfile);
        
        Object.entries(userProfile).forEach(([key, value]) => {
          setValue(key as keyof UserProfile, value);
        });
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError("Failed to load profile data");
        toast({
          title: "Error",
          description: "Failed to load profile data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [setValue, toast]);

  const onSubmit = async (data: UserProfile) => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error("Not authenticated");

      const { error: updateError } = await supabase
        .from('users')
        .update({
          first_name: data.first_name,
          last_name: data.last_name,
          username: data.username,
          bio: data.bio,
          email: data.email,
          phone_number: data.phone_number,
          location: data.location,
          website: data.website,
          company_name: data.company_name,
          notification_preferences: data.notification_preferences as unknown as Json,
          privacy_settings: data.privacy_settings as unknown as Json,
          language: data.language,
          timezone: data.timezone,
          music_preferences: data.music_preferences,
          event_preferences: data.event_preferences,
          location_preferences: data.location_preferences,
          booking_policy: data.booking_policy,
          stage_name: data.stage_name,
          press_kit_url: data.press_kit_url,
          streaming_links: data.streaming_links,
          social_links: data.social_links,
          payment_details: data.payment_details as unknown as Json,
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      
      setProfile(data);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="text-center text-red-500">
          <h2 className="text-xl font-semibold mb-2">Error Loading Profile</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-5xl space-y-8">
        <Skeleton className="h-48 w-full mb-6" />
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-24 w-full mb-6" />
        <div className="grid md:grid-cols-2 gap-6">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <ProfileForm
        register={register}
        profile={profile}
        isLoading={isLoading}
        onSubmit={handleSubmit(onSubmit)}
        onUpload={handleMediaUpload}
        watch={watch}
        setValue={setValue}
      />
    </div>
  );
};
